import { DynamicPage, DynamicPageTag } from '@betterfunnel/types';
import * as Sentry from '@sentry/react';
import { diff } from 'deep-diff';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import languages from '../../../assets/languages.json';
import AlertModal from '../../../components/Common/AlertModal';
import Button from '../../../components/Common/Button';
import SettingSectionAutocomplete from '../../../components/settings/SettingSectionAutocomplete';
import SettingSectionChipsField from '../../../components/settings/SettingSectionChipsField';
import SettingSectionTextField from '../../../components/settings/SettingSectionTextField';
import SettingSectionTitle from '../../../components/settings/SettingSectionTitle';
import { dynamicPageGoalsMap } from '../../../constants/dynamic-page';
import { DashboardContext } from '../../../contexts/DashboardContext';
import { SnackbarContext } from '../../../contexts/SnackbarContext';
import { UserContext } from '../../../contexts/UserContext';
import {
  deleteDynamicPage,
  updateDynamicPage,
  updateDynamicPageMetadata,
} from '../../../services/DynamicPagesService';
import { track } from '../../../utils/analytics';

export const ManageDynamicPageSettingsGeneral = () => {
  const navigate = useNavigate();

  const { show } = useContext(SnackbarContext);
  const { selectedAccount } = useContext(UserContext);
  const { selectedDynamicPage, dynamicPages, domains } =
    useContext(DashboardContext);

  const [name, setName] = useState('');
  const [path, setPath] = useState('');
  const [alternatePathes, setAlternatePathes] = useState<string[]>([]);
  const [websiteLink, setWebsiteLink] = useState<string>('');
  const [language, setLanguage] = useState<(typeof languages)[0]>();
  const [tags, setTags] = useState<DynamicPageTag[]>();
  const [selectedDomains, setSelectedDomains] = useState<string[]>();
  const [urlSuffix, setUrlSuffix] = useState<string>('');
  const [deleteConfirmationModalOpen, setDeleteConfirmationModalOpen] =
    useState(false);

  const tagOptions = useMemo(() => {
    const tagsMap = new Map<string, DynamicPageTag>();
    dynamicPages?.forEach((c) =>
      c.data().tags?.forEach((t) => tagsMap.set(t.id, t)),
    );

    return Array.from(tagsMap.values()).map((t) => ({
      id: t.id,
      title: t.name,
    }));
  }, [dynamicPages]);

  useEffect(() => {
    if (!selectedDynamicPage) return;

    setName(selectedDynamicPage?.data().name || '');
    setPath(selectedDynamicPage?.data().path || '');
    setAlternatePathes(selectedDynamicPage?.data().alternatePathes || []);
    setWebsiteLink(selectedDynamicPage?.data()?.config?.websiteLink || '');
    setLanguage(selectedDynamicPage?.data()?.language);
    setTags(selectedDynamicPage?.data()?.tags);
    setSelectedDomains(
      selectedDynamicPage?.data()?.domains ||
        domains?.map((d) => d.data().domain) ||
        [],
    );
    setUrlSuffix(selectedDynamicPage?.data()?.config?.urlSuffix || '');
  }, [domains, selectedDynamicPage]);

  const updateDynamicPageData = useCallback(
    async (data: any) => {
      if (!selectedAccount || !selectedDynamicPage) return;
      try {
        await updateDynamicPage(
          selectedAccount.id,
          selectedDynamicPage.id,
          data,
        );
        show('Updated');
      } catch (_) {
        show('Failed to update');
      }
    },
    [selectedAccount, selectedDynamicPage, show],
  );

  const updateName = useCallback(async () => {
    if (!selectedDynamicPage) return;
    if (selectedDynamicPage.data().name === name) return;

    updateDynamicPageData({ name });
  }, [selectedDynamicPage, name, updateDynamicPageData]);

  const updateLanguage = useCallback(async () => {
    if (!selectedDynamicPage) return;
    if (selectedDynamicPage.data().language?.code === language?.code) return;

    updateDynamicPageData({ language });
  }, [selectedDynamicPage, language, updateDynamicPageData]);

  const updatePath = useCallback(async () => {
    if (!selectedDynamicPage || !selectedAccount) return;
    if (selectedDynamicPage.data().path === path) return;

    const oldPath = selectedDynamicPage.data().path;
    return updateDynamicPageMetadata(
      selectedAccount.id,
      selectedDynamicPage.id,
      {
        path,
      },
    ).catch((e) => {
      Sentry.captureException(e);
      show('Failed to update path');
      setPath(oldPath);
      console.error(e);
    });
  }, [selectedDynamicPage, selectedAccount, path, show]);

  const updateAlternatePathes = useCallback(async () => {
    if (!selectedDynamicPage || !selectedAccount) return;
    if (
      !diff(selectedDynamicPage.data().alternatePathes, alternatePathes)?.length
    ) {
      return;
    }

    const oldAlternatePathes = selectedDynamicPage.data().alternatePathes;
    return updateDynamicPageMetadata(
      selectedAccount.id,
      selectedDynamicPage.id,
      {
        alternatePathes,
      },
    ).catch((e) => {
      show(e?.message || 'Failed to update alternate pathes');
      setAlternatePathes(oldAlternatePathes ?? []);
      Sentry.captureException(e);
      console.error(e);
    });
  }, [selectedDynamicPage, selectedAccount, alternatePathes, show]);

  const updateWebsiteLink = useCallback(async () => {
    if (!selectedDynamicPage) return;
    if (selectedDynamicPage.data().config?.websiteLink === websiteLink) return;

    updateDynamicPageData({
      config: {
        ...selectedDynamicPage.data().config,
        websiteLink,
      },
    });
  }, [selectedDynamicPage, updateDynamicPageData, websiteLink]);

  const updateUrlSuffix = useCallback(async () => {
    if (!selectedDynamicPage) return;
    if (selectedDynamicPage.data().config?.urlSuffix === urlSuffix) return;

    updateDynamicPageData({
      config: {
        ...selectedDynamicPage.data().config,
        urlSuffix,
      },
    });
  }, [selectedDynamicPage, updateDynamicPageData, urlSuffix]);

  const updateCampaignTags = useCallback(async () => {
    if (!selectedDynamicPage) return;

    updateDynamicPageData({
      tags: tags,
    });
  }, [selectedDynamicPage, tags, updateDynamicPageData]);

  const updateCampaignDomains = useCallback(async () => {
    if (!selectedDomains?.length) {
      show('At least one domain is required');
      return;
    }

    updateDynamicPageData({
      domains: selectedDomains,
    });
  }, [selectedDomains, show, updateDynamicPageData]);

  const onDeleteDynamicPageConfirm = useCallback(() => {
    if (!selectedAccount || !selectedDynamicPage) return;

    track('delete_dynamic_page_modal_approval_button_click', {
      id: selectedDynamicPage.id,
    });

    deleteDynamicPage(selectedAccount.id, selectedDynamicPage.id);
    setDeleteConfirmationModalOpen(false);
    navigate('../../..');
  }, [navigate, selectedAccount, selectedDynamicPage]);

  const campaignData: DynamicPage | undefined = selectedDynamicPage?.data();

  return (
    <div>
      <AlertModal
        type={'warn'}
        title="Are you sure you'd like to delete this Dynamic Page?"
        description="This is not reversible"
        open={deleteConfirmationModalOpen}
        onClose={() => setDeleteConfirmationModalOpen(false)}
        actions={[
          {
            title: 'Cancel',
            type: 'secondary',
            onClick: () => setDeleteConfirmationModalOpen(false),
          },
          {
            title: 'Delete',
            type: 'primary',
            color: 'negative',
            onClick: onDeleteDynamicPageConfirm,
          },
        ]}
      />
      <SettingSectionTitle
        title="Dynamic Page"
        description="Dynamic Page general settings."
      />

      <dl className="mt-6 space-y-6 divide-y divide-gray-100 border-t border-gray-200 text-sm leading-6">
        <SettingSectionTextField
          title="Name"
          value={name}
          setValue={setName}
          update={updateName}
          editable={true}
        />
        <SettingSectionTextField
          title="Path"
          value={path}
          setValue={setPath}
          update={updatePath}
          editable={true}
        />
        <SettingSectionChipsField
          title="Alternate Pathes"
          selected={alternatePathes?.map((p) => ({ id: p, title: p })) ?? []}
          setSelected={(selected) => {
            setAlternatePathes(selected.map((t) => t.title));
          }}
          update={updateAlternatePathes}
          editable
          options={[]}
          filterQuery={(t) =>
            t
              .toLocaleLowerCase()
              .trim()
              .replaceAll(/-+/g, '-')
              .replaceAll(/[^a-z0-9-]/g, '')
          }
        />
        <div className="pt-6 sm:flex">
          <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">
            Goal
          </dt>
          <dd className="mt-1 flex justify-between gap-x-6 sm:mt-0 sm:flex-auto">
            <div className="text-gray-900">
              {campaignData?.goal ? dynamicPageGoalsMap[campaignData.goal] : ''}
            </div>
          </dd>
        </div>
        <SettingSectionTextField
          title="Logo link"
          value={websiteLink}
          setValue={setWebsiteLink}
          update={updateWebsiteLink}
          editable={true}
        />
        <SettingSectionChipsField
          title="Tags"
          selected={tags?.map((t) => ({ id: t.id, title: t.name })) ?? []}
          setSelected={(selected) => {
            setTags(selected.map((t) => ({ id: t.id, name: t.title })));
          }}
          update={updateCampaignTags}
          editable
          options={tagOptions}
        />
        <SettingSectionChipsField
          title="Domains"
          selected={selectedDomains?.map((t) => ({ id: t, title: t })) ?? []}
          setSelected={(selected) => {
            setSelectedDomains(selected.map((t) => t.id));
          }}
          update={updateCampaignDomains}
          editable
          options={
            domains?.map((d) => ({
              id: d.data().domain,
              title: d.data().domain,
            })) ?? []
          }
        />
        <SettingSectionAutocomplete
          title="Language"
          selected={
            language ??
            languages.find((l) => l.code === 'en-US') ??
            languages[0]
          }
          getValue={(o) => o.name}
          getOptionId={(o) => o.code}
          setSelected={(selected) => {
            setLanguage(selected || undefined);
          }}
          update={updateLanguage}
          editable
          options={languages}
        />
      </dl>

      <div className="mt-10">
        <SettingSectionTitle
          title="Custom URL Suffix"
          description="Custom URL Suffix for the Dynamic Page, added to the end of all your links."
        />

        <dl className="mt-6 space-y-6 divide-y divide-gray-100 border-t border-gray-200 text-sm leading-6">
          <SettingSectionTextField
            title="URL Suffix"
            value={urlSuffix}
            setValue={setUrlSuffix}
            update={updateUrlSuffix}
            editable={true}
            pattern="^([a-zA-Z0-9]+=(?:[a-zA-Z0-9]+|\{\{[a-zA-Z0-9]+\}\}))(?:&[a-zA-Z0-9]+=(?:[a-zA-Z0-9]+|\{\{[a-zA-Z0-9]+\}\}))*$"
          />
        </dl>
      </div>

      <div className="mt-10">
        <SettingSectionTitle title="Danger Zone" description="Beware 😈" />

        <dl className="mt-6 space-y-6 divide-y divide-gray-100 border-t border-gray-200 text-sm leading-6">
          <div className="pt-6 sm:flex">
            <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">
              Delete Dynamic Page
            </dt>
            <dd className="mt-1 flex justify-end gap-x-6 sm:mt-0 sm:flex-auto">
              <Button
                variant="secondary"
                color="negative"
                onClick={() => {
                  track('delete_dynamic_page_button_click', {
                    id: selectedDynamicPage?.id,
                  });
                  setDeleteConfirmationModalOpen(true);
                }}
              >
                Delete Dynamic Page
              </Button>
            </dd>
          </div>
        </dl>
      </div>
    </div>
  );
};
